import React, { useEffect, useState } from 'react';
import { ScheduleService } from '../../service/ScheduleService';
import { Button, Select, TextField } from '@mui/material';
import KitModal from '../UI/KitModal';
import useFlowsStore from '../../store/storeFlows';
import { generateCustomID } from '../../util/generateCustomId';
import { useSocketStore } from '../../store/storeSocket';
import { useNotificationStore } from '../../store/storeNotifications';
import { useAuthStore } from '../../store/storeAuth';

const intervalTypes = ['hourly', 'daily', 'weekly', 'monthly'];
const intervalWeekdays = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const AutoTriggerTimerModal = ({
  isAutoTriggerModalOpen,
  setIsAutoTriggerModalOpen,
  formData,
  setFormData,
  data,
  intervalData,
  setIntervalData,
  id,
  saveNode,
}: any) => {
  const { setNodeState, nodes, flowId } = useFlowsStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const { setProcessingFlows, setDeleteProcessingFlow } = useSocketStore(
    state => state,
  );
  const [schedule, setSchedule] = useState<any>({});
  const textVariables = nodes?.filter((node: { id: string | string[] }) =>
    node.id.includes('varInputNode'),
  );
  useEffect(() => {
    const getSchedule = async () => {
      try {
        const res: any = await ScheduleService.getSchedules();
        const flowSchedule = res?.data?.find(
          (schedule: { flowId: string }) => schedule.flowId === flowId,
        );
        if (flowSchedule?.flowId === flowId && formData.isAutotrigger) {
          setProcessingFlows(flowId);
        }
        setSchedule(flowSchedule);
      } catch (error) {}
    };
    getSchedule();
  }, [
    formData.isAutotrigger,
    isAutoTriggerModalOpen,
    setIsAutoTriggerModalOpen,
    flowId,
  ]);

  const onChange = (e: { target: { value: string; name: string } }) => {
    if (
      e.target.name === 'daily' ||
      e.target.name === 'hourly' ||
      e.target.name === 'weekly' ||
      e.target.name === 'monthly'
    ) {
      setIntervalData(
        (prev: { hourly: any; daily: any; monthly: any; weekly: any }) => ({
          ...prev,
          [e.target.name]: e.target.value,
          codedTime: `${
            e.target.name === 'hourly' ? e.target.value : prev.hourly
          } ${e.target.name === 'daily' ? e.target.value : prev.daily} ${
            e.target.name === 'monthly' ? e.target.value : prev.monthly
          } * ${e.target.name === 'weekly' ? e.target.value : prev.weekly}`,
        }),
      );
    } else {
      setIntervalData((prev: { hourly: any; daily: any }) => ({
        ...prev,
        [e.target.name]: e.target.value,
        weekly: '*',
        // monthly: '*',
        codedTime: `${prev?.hourly ? prev?.hourly : '*'} ${
          prev?.daily ? prev?.daily : '*'
        } * * *`,
      }));
    }
  };

  const getTriggerMessage = (type: string) => {
    if (type === 'hourly') {
      return `Auto trigger is activated ${intervalData?.hourly} minutes past the hour `;
    } else if (type === 'daily') {
      return `Auto trigger is activated every day at ${intervalData?.daily} : ${intervalData?.hourly}`;
    } else if (type === 'weekly') {
      return `Auto trigger is activated every ${intervalData?.weekly} at ${intervalData?.daily} : ${intervalData?.hourly}`;
    } else if (type === 'monthly') {
      return `Auto trigger is activated at the ${intervalData?.monthly} day of the month, at ${intervalData?.daily} : ${intervalData?.hourly}`;
    } else {
      return '';
    }
  };
  const codedTime = (interval: string) => {
    const weekDays: { [key: string]: number | string } = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 7,
      ['*']: '*',
    };

    const intervalArray = interval.split(' ');

    const intervalData = {
      minute: intervalArray?.[0],
      hour: intervalArray?.[1],
      day: intervalArray?.[2],
      month: intervalArray?.[3],
      weekDay: intervalArray?.[4],
    };

    return `${intervalData.minute} ${intervalData.hour} ${intervalData.day} ${
      intervalData.month
    } ${weekDays[intervalData.weekDay]}`;
  };

  const onSave = async () => {
    try {
      if (schedule?.id) {
        await ScheduleService.updateSchedule({
          name: schedule.name,
          id: schedule.id,
          interval: codedTime(intervalData.codedTime),
        });
      } else {
        let objectData = {};

        if (intervalData.varInput) {
          objectData = {
            variableInputs: {
              inputId: intervalData.varInput,
              inputText: intervalData.inputText,
            },
          };
        }

        await ScheduleService.addSchedule({
          name: 'Flow Global Node' + generateCustomID(),
          interval: codedTime(intervalData.codedTime),
          flowId: flowId,
          webhook: false,
          ...objectData,
        });
        setProcessingFlows(flowId);
      }

      saveNode({
        triggerMessage: getTriggerMessage(intervalData.type),
        intervalData: intervalData,
        isAutotrigger: true,
      });

      setFormData((prev: any) => ({
        ...prev,
        triggerMessage: getTriggerMessage(intervalData.type),
        intervalData: intervalData,
        isAutotrigger: true,
      }));
      setIsAutoTriggerModalOpen(false);
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error?.message,
      });
    }
  };

  const onDelete = async () => {
    try {
      setDeleteProcessingFlow(flowId);
      await ScheduleService.deleteSchedule(schedule?.id);
      setIsAutoTriggerModalOpen(false);
      setFormData((prev: any) => ({
        ...prev,
        isAutotrigger: false,
      }));
      saveNode({
        triggerMessage: '',
        intervalData: {},
        isAutotrigger: false,
      });
    } catch (error) {
      setIsAutoTriggerModalOpen(false);
      setFormData((prev: any) => ({
        ...prev,
        isAutotrigger: false,
      }));
    }
  };

  const generateDays = () => {
    const daysInMonth = new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ).getDate();

    const daysArray = Array.from(
      { length: daysInMonth },
      (_, index) => index + 1,
    );

    return daysArray;
  };

  const hourlyInput = () => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        e.target.value.trim() === '' ? null : parseInt(e.target.value, 10);

      // Check if the value is within the valid range (1 to 59) or null
      if (value === null || (!isNaN(value) && value >= 1 && value <= 59)) {
        onChange({
          target: {
            value: value === null ? '' : value.toString(),
            name: 'hourly',
          },
        });
      }
    };

    return (
      <>
        <TextField
          variant="outlined"
          style={{
            maxWidth: '500px',
            // border: `1px solid ${
            //   user?.theme === 'dark' ? '#344054' : '#D0D5DD'
            // }`,
            backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            color: user?.theme === 'dark' ? '#98A2B3' : 'black',
          }}
          onChange={handleChange}
          value={intervalData?.hourly}
          type="number"
          name="hourly"
          // min="1"
          // max="59"
        />
      </>
    );
  };

  const dailyInput = (showLabel: boolean) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        e.target.value.trim() === '' ? null : parseInt(e.target.value, 10);

      // Check if the value is within the valid range (1 to 59) or null
      if (value === null || (!isNaN(value) && value >= 1 && value <= 23)) {
        onChange({
          target: {
            value: value === null ? '' : value.toString(),
            name: 'daily',
          },
        });
      }
    };
    return (
      <>
        {/* {showLabel && (
          <label
            style={{
              color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
            }}
          >
            Every day at:
          </label>
        )} */}
        <TextField
          // label=" Every day at"
          variant="outlined"
          sx={{
            maxWidth: '500px',
            // border: `1px solid ${
            //   user?.theme === 'dark' ? '#344054' : '#D0D5DD'
            // }`,
            backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            color: user?.theme === 'dark' ? '#98A2B3' : 'black',
          }}
          onChange={handleChange}
          value={intervalData?.daily}
          type="number"
          name="daily"
          // min="1"
          // max="59"
        />
      </>
    );
  };
  const monthlyInput = () => {
    return (
      <>
        <label
          style={{
            color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
          }}
        >
          Every&nbsp;
        </label>
        <select
          value={intervalData.monthly}
          name="monthly"
          onChange={onChange}
          style={{
            backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            height: '40px',
            borderRadius: '5px',
            padding: '0 10px',
            // border: `2px solid ${
            //   user?.theme === 'dark' ? '#475467' : '#EAECF0'
            // }`,
          }}
        >
          <option key={'choose'} value={'*'}>
            Choose
          </option>
          {generateDays().map(type => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </>
    );
  };

  const { user } = useAuthStore(state => state);

  return (
    <div>
      <KitModal
        show={isAutoTriggerModalOpen}
        onHide={() => {
          setIsAutoTriggerModalOpen(false);
          setFormData((prev: any) => ({
            ...prev,
            isAutotrigger: schedule?.flowId === flowId,
          }));
        }}
        title="Set time"
        footer={
          <>
            <Button onClick={onSave} variant="contained" sx={{ mr: '15px' }}>
              {schedule?.id ? 'Update' : 'Save'}
            </Button>
            {schedule?.id && <Button onClick={onDelete}>Delete</Button>}
          </>
        }
      >
        <>
          {/* {schedule?.flowId !== flowId && (
            <>
              <div
                style={{
                  marginBottom: '20px',
                }}
              >
                <p
                  style={{
                    margin: '0',
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Input variable
                </p>
                <select
                  value={intervalData?.varInput}
                  name="varInput"
                  onChange={onChange}
                  style={{
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                >
                  <option value={''}>Choose</option>
                  {textVariables.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.data.label}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  marginBottom: '20px',
                }}
              >
                <p
                  style={{
                    margin: '0',
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Input Text
                </p>
                <TextField
                  name="inputText"
                  value={intervalData?.inputText}
                  onChange={onChange}
                  InputProps={{
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </div>
            </>
          )} */}

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <select
              value={intervalData?.type}
              name="type"
              onChange={onChange}
              style={{
                backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                height: '40px',
                borderRadius: '5px',
                padding: '0 10px',
                // border: `2px solid ${
                //   user?.theme === 'dark' ? '#475467' : '#EAECF0'
                // }`,
              }}
            >
              {intervalTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {intervalData?.type === 'hourly' && hourlyInput()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {intervalData?.type === 'daily' && (
                <>
                  {' '}
                  {dailyInput(true)}&nbsp;-&nbsp;{hourlyInput()}
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {intervalData?.type === 'weekly' && (
                <>
                  <label
                    style={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    Every&nbsp;
                  </label>
                  <select
                    value={intervalData.weekly}
                    name="weekly"
                    onChange={onChange}
                    style={{
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      height: '40px',
                      borderRadius: '5px',
                      padding: '0 10px',
                      // border: `2px solid ${
                      //   user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      // }`,
                    }}
                  >
                    <option value={'*'}>Choose</option>
                    {intervalWeekdays.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <label
                    style={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    &nbsp;at:&nbsp;
                  </label>
                  {dailyInput(false)}&nbsp;-&nbsp;{hourlyInput()}
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {intervalData?.type === 'monthly' && (
                <>
                  {monthlyInput()}
                  <label
                    style={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    &nbsp;at:&nbsp;
                  </label>
                  {dailyInput(false)}&nbsp;-&nbsp;{hourlyInput()}
                </>
              )}
            </div>
          </div>
        </>
      </KitModal>
    </div>
  );
};

export default AutoTriggerTimerModal;
